(function productExperienceRuntimeModule(root, factory) {
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    root.TACProductExperience = Object.freeze(api);
    api.initialize(root);
  }
})(typeof window !== 'undefined' ? window : globalThis, function productExperienceRuntimeFactory(root) {
  'use strict';

  const THEME_ID = '161038008367';
  const THEME_IDS = Object.freeze([THEME_ID, '155071873185']);
  const API_BASES = Object.freeze({
    staging: 'https://tac-files-api-staging.uralbum-cool.workers.dev',
    production: 'https://tac-files-api.uralbum-cool.workers.dev'
  });
  const ASSET_HOSTS = Object.freeze({
    staging: Object.freeze(['cdn.shopify.com', 'pub-0386f676558d45c2be944f6fd7820b2d.r2.dev']),
    production: Object.freeze(['cdn.shopify.com', 'tac-theme-assets.uralbum-cool.workers.dev'])
  });
  const RC_FLAG = 'tac3crc';
  const CREATE_KEY_PREFIX = 'tac:product-experience-create:v1:';
  const COPY_ALIASES = Object.freeze({
    'steps.cover.title': ['step_title_portada'],
    'steps.dedication.title': ['step_title_dedicatoria', 'dedicatoria_title'],
    'steps.final_text.title': ['step_title_texto_final', 'texto_final_title'],
    'steps.stamps.title': ['step_title_fotos_estampas'],
    'steps.captioned.title': ['step_title_fotos_con_texto'],
    'steps.puzzle.title': ['step_title_foto_rompecabezas'],
    'steps.spotify_codes.title': ['step_title_spotify_codigos'],
    'steps.playlist.title': ['step_title_spotify_playlist'],
    'steps.backgrounds.title': ['step_title_fotos_fondo'],
    'steps.movies.title': ['step_title_peliculas'],
    'steps.words.title': ['step_title_sopa_letras'],
    'steps.date.title': ['step_title_fecha_especial'],
    'steps.review.title': ['step_title_resumen'],
    'steps.checkout.title': ['step_title_checkout'],
    'steps.cover.phrase_placeholder': ['portada_placeholder_frase'],
    'steps.cover.names_placeholder': ['portada_placeholder_nombres'],
    'steps.cover.date_placeholder': ['portada_placeholder_fecha'],
    'steps.cover.photo_tip': ['tip_portada_foto'],
    'steps.cover.phrase_tip': ['tip_portada_frase'],
    'steps.dedication.description': ['dedicatoria_desc'],
    'steps.dedication.placeholder': ['dedicatoria_placeholder_de_parte'],
    'steps.final_text.description': ['texto_final_desc'],
    'steps.final_text.placeholder': ['texto_final_placeholder'],
    'steps.stamps.prompt': ['fotos_estampas_title'],
    'steps.stamps.tip': ['tip_fotos_estampas'],
    'steps.captioned.prompt': ['fotos_con_texto_title'],
    'steps.captioned.placeholder': ['fotos_con_texto_placeholder_texto'],
    'steps.captioned.tip': ['tip_fotos_con_texto'],
    'steps.puzzle.prompt': ['foto_rompecabezas_title'],
    'steps.puzzle.tip': ['tip_foto_rompecabezas'],
    'steps.spotify_codes.prompt': ['spotify_codigos_title'],
    'steps.spotify_codes.placeholder': ['spotify_codigos_item_placeholder'],
    'steps.spotify_codes.tip': ['tip_spotify_codigos'],
    'steps.playlist.prompt': ['spotify_playlist_title'],
    'steps.playlist.placeholder': ['spotify_playlist_placeholder'],
    'steps.playlist.tip': ['spotify_playlist_tip_use_link', 'spotify_playlist_tip_copy_link'],
    'steps.backgrounds.prompt': ['fotos_fondo_title'],
    'steps.backgrounds.tip': ['tip_fotos_fondo'],
    'steps.movies.prompt': ['peliculas_title'],
    'steps.movies.placeholder': ['peliculas_item_placeholder'],
    'steps.movies.tip': ['tip_peliculas'],
    'steps.words.prompt': ['sopa_letras_title'],
    'steps.words.placeholder': ['sopa_letras_item_placeholder'],
    'steps.words.tip': ['tip_sopa_letras'],
    'steps.date.prompt': ['fecha_especial_title'],
    'steps.date.placeholder': ['fecha_especial_placeholder'],
    'steps.date.tip': ['tip_fecha_especial']
  });
  const COPY_STEP_IDS = Object.freeze({
    stamps: 'fotos_estampas', captioned: 'fotos_con_texto', puzzle: 'foto_rompecabezas',
    spotify_codes: 'spotify_codigos', playlist: 'spotify_playlist', backgrounds: 'fotos_fondo',
    movies: 'peliculas', words: 'sopa_letras', date: 'fecha_especial'
  });
  const EXAMPLE_RENDERER_KEYS = Object.freeze({
    dedication: Object.freeze(['dedication', 'dedicatoria']),
    final_text: Object.freeze(['final_text', 'texto_final']),
    captioned: Object.freeze(['captioned', 'fotos_con_texto']),
    puzzle: Object.freeze(['puzzle', 'foto_rompecabezas']),
    spotify_codes: Object.freeze(['spotify_codes', 'spotify_codigos']),
    spotify_playlist: Object.freeze(['spotify_playlist']),
    movies: Object.freeze(['movies', 'peliculas']),
    word_search: Object.freeze(['word_search', 'sopa_letras']),
    special_date: Object.freeze(['special_date', 'fecha_especial']),
    backgrounds: Object.freeze(['backgrounds', 'fotos_fondo'])
  });
  const EXAMPLE_SEMANTIC_BY_RENDERER = Object.freeze(Object.fromEntries(
    Object.entries(EXAMPLE_RENDERER_KEYS).flatMap(([semantic, keys]) => keys.map(key => [key, semantic]))
  ));
  let candidate = null;
  let activeExperience = null;

  function runtimeProduct(runtime) {
    const handle = String(runtime?.product_handle || '').trim().toLowerCase();
    const gid = String(runtime?.product_gid || '').trim();
    const environment = String(runtime?.environment || '').trim().toLowerCase();
    const apiBase = String(runtime?.api_base_url || '').replace(/\/+$/, '');
    if (!/^[a-z0-9][a-z0-9-]*$/.test(handle) || !/^gid:\/\/shopify\/Product\/[1-9][0-9]*$/.test(gid) ||
        !API_BASES[environment] || apiBase !== API_BASES[environment]) return null;
    return Object.freeze({ handle, gid, environment, apiBase });
  }

  function shouldRequestAdoption(runtime, locationLike, route = 'shopify') {
    if (route !== 'shopify' || !runtimeProduct(runtime)) return false;
    if (String(runtime?.rollback_reader_only || '') === 'true') return true;
    if (String(runtime?.rc_compatibility || '') !== 'true') return true;
    const role = String(runtime?.theme_role || '').toLowerCase();
    if (!THEME_IDS.includes(String(runtime?.theme_id || '')) || !['development', 'unpublished'].includes(role)) return false;
    try { return new URL(locationLike.href).searchParams.get(RC_FLAG) === '1'; }
    catch { return false; }
  }

  function runtimeUrl(runtime) {
    const product = runtimeProduct(runtime);
    if (!product) throw new Error('experience_runtime_configuration_invalid');
    const compatibility = String(runtime?.rc_compatibility || '') === 'true' ||
      String(runtime?.rollback_reader_only || '') === 'true';
    const url = new URL(`${product.apiBase}/v1/product-experience/${compatibility ? 'compatibility' : 'runtime'}`);
    url.searchParams.set('channel', 'shopify');
    if (compatibility) {
      url.searchParams.set('theme_id', String(runtime.theme_id));
      url.searchParams.set('theme_role', String(runtime.theme_role).toLowerCase());
    }
    url.searchParams.set('product_gid', product.gid);
    url.searchParams.set('handle', product.handle);
    url.searchParams.set('locale', String(runtime.locale || 'es').toLowerCase());
    return url.toString();
  }

  function assertExperience(value) {
    if (!value || value.resolver_class !== 'product_experience_v1' || value.authority !== 'PINNED' ||
        !['staging', 'production'].includes(String(value.environment || '')))
      throw new Error('experience_runtime_payload_invalid');
    const compatibilityValid = value.legacy_parity === 'MATCH' ||
      value.compatibility_mode === 'NATIVE_EXACT_RELEASE';
    if (!/^PR-[a-f0-9]{64}$/i.test(String(value.release?.release_record_id || '')) ||
        !/^[a-f0-9]{64}$/i.test(String(value.release?.release_sha256 || '')) ||
        !/^PB-[a-f0-9]{64}$/i.test(String(value.binding?.binding_id || '')) ||
        !Number.isSafeInteger(Number(value.binding?.binding_revision)) ||
        !Number.isSafeInteger(Number(value.binding?.activation_revision)) ||
        !compatibilityValid || value.projection?.root?.channel !== 'shopify')
      throw new Error('experience_runtime_authority_invalid');
    if (!Array.isArray(value.projection?.resolved_steps) || !value.assets || typeof value.assets !== 'object')
      throw new Error('experience_runtime_projection_invalid');
    const allowedHosts = ASSET_HOSTS[value.environment];
    for (const asset of Object.values(value.assets)) {
      let url;
      try { url = new URL(String(asset?.url || '')); } catch { throw new Error('experience_runtime_asset_invalid'); }
      if (url.protocol !== 'https:' || !allowedHosts.includes(url.hostname) || url.username || url.password ||
          !/^[a-f0-9]{64}$/i.test(String(asset?.sha256 || ''))) throw new Error('experience_runtime_asset_invalid');
    }
    return value;
  }

  function publicState(resolver = activeExperience ? 'PRODUCT_EXPERIENCE' : 'LEGACY') {
    const experience = activeExperience;
    const root = experience?.projection?.root;
    return Object.freeze({
      runtime_resolver: resolver,
      authority: experience ? 'PINNED' : 'LEGACY',
      release: experience?.release?.release_record_id || null,
      release_sha256: experience?.release?.release_sha256 || null,
      binding: experience?.binding?.binding_id || null,
      binding_revision: experience ? Number(experience.binding.binding_revision) : null,
      activation_at_creation: experience ? Number(experience.binding.activation_revision) : null,
      legacy_parity: experience?.legacy_parity || null,
      compatibility_mode: experience?.compatibility_mode || null,
      root_id: root?.id || null,
      root_version: root ? Number(root.version) : null,
      theme_pack: experience?.projection?.theme_pack?.id
        ? `${experience.projection.theme_pack.id}.v${experience.projection.theme_pack.version}` : null,
      package_rules: experience?.projection?.package_rules?.id
        ? `${experience.projection.package_rules.id}.v${experience.projection.package_rules.version}` : null,
      step_flow: experience?.projection?.step_flow?.id
        ? `${experience.projection.step_flow.id}.v${experience.projection.step_flow.version}` : null
    });
  }

  function announce(win) {
    win.TAC_PRODUCT_EXPERIENCE_STATE = publicState();
    if (typeof win.document?.dispatchEvent === 'function' && typeof win.CustomEvent === 'function') {
      win.document.dispatchEvent(new win.CustomEvent('tac:product-experience-runtime', {
        detail: win.TAC_PRODUCT_EXPERIENCE_STATE
      }));
    }
  }

  function projectedCopy(experience, locale) {
    const source = experience.projection.localized_content?.copy || {};
    const output = { ...source };
    const steps = new Map((experience.projection.resolved_steps || []).map(step => [step.id, step]));
    for (const [key, aliases] of Object.entries(COPY_ALIASES)) {
      if (!(key in source)) continue;
      const semantic = key.split('.')[1];
      const step = steps.get(COPY_STEP_IDS[semantic] || semantic);
      const count = Number(step?.parameters?.count ?? step?.parameters?.suggested_tracks);
      const required = step?.required === false ? '' : (String(locale).startsWith('en') ? ' (required)' : ' (obligatorio)');
      const value = String(source[key])
        .replaceAll('{count}', Number.isSafeInteger(count) && count > 0 ? String(count) : '')
        .replaceAll('{required}', required)
        .replaceAll('{index}', '{{i}}');
      for (const alias of aliases) output[alias] = value;
    }
    return output;
  }

  function assetOrigins(experience = activeExperience) {
    if (!experience?.assets || typeof experience.assets !== 'object') return [];
    return [...new Set(Object.values(experience.assets).map(asset => {
      try {
        const url = new URL(String(asset?.url || ''));
        return url.protocol === 'https:' && ASSET_HOSTS[experience.environment]?.includes(url.hostname) ? url.origin : null;
      } catch { return null; }
    }).filter(Boolean))].sort();
  }

  function primeAssetOrigins(experience = activeExperience, win = root) {
    const doc = win?.document;
    if (!doc?.head || typeof doc.createElement !== 'function') return [];
    const origins = assetOrigins(experience);
    const existing = Array.from(doc.querySelectorAll?.('link[data-tac-asset-origin]') || []);
    for (const origin of origins) {
      if (existing.some(link => link.getAttribute?.('href') === origin)) continue;
      const link = doc.createElement('link');
      link.rel = 'preconnect';
      link.href = origin;
      link.crossOrigin = 'anonymous';
      link.setAttribute('data-tac-asset-origin', 'preconnect');
      doc.head.appendChild(link);
      existing.push(link);
    }
    return origins;
  }

  function resolvedExampleImages(experience = activeExperience) {
    if (!experience) return Object.freeze({});
    const localized = experience.projection?.localized_content?.assets || {};
    const output = {};
    for (const [semantic, rendererKeys] of Object.entries(EXAMPLE_RENDERER_KEYS)) {
      const assetId = localized[`examples.${semantic}`];
      const url = experience.assets?.[assetId]?.url;
      if (!url) continue;
      for (const key of rendererKeys) output[key] = url;
    }
    return Object.freeze(output);
  }

  function exampleImage(key, fallback = '', win = root) {
    const normalized = String(key || '').trim();
    const pinned = activeExperience && win.TAC_PRODUCT_EXPERIENCE_STATE?.authority === 'PINNED';
    if (pinned) return resolvedExampleImages(activeExperience)[normalized] || '';
    const locale = String(win.tacLang || 'es').toLowerCase().startsWith('en') ? 'en' : 'es';
    return win.TAC_EXAMPLE_IMAGES?.[locale]?.[normalized] || fallback || '';
  }

  function applyResolvedExperience(value, win = root) {
    activeExperience = assertExperience(value);
    primeAssetOrigins(activeExperience, win);
    const locale = activeExperience.projection.localized_content?.resolved_locale || 'es';
    win.tacCopy ||= {};
    win.tacCopy[locale] ||= {};
    Object.assign(win.tacCopy[locale], projectedCopy(activeExperience, locale));
    win.TAC_EXAMPLE_IMAGES ||= {};
    win.TAC_EXAMPLE_IMAGES[locale] ||= {};
    Object.assign(win.TAC_EXAMPLE_IMAGES[locale], resolvedExampleImages(activeExperience));
    announce(win);
    return activeExperience;
  }

  function stepForRenderer(step) {
    const result = { step: step.id, required: step.required === true };
    const count = Number(step.parameters?.count ?? step.parameters?.suggested_tracks);
    if (Number.isSafeInteger(count) && count > 0) result.cantidad = count;
    if (step.id === 'portada') result.fields = ['diseño', 'texto', 'foto'];
    return result;
  }

  function adaptProduct(legacyProduct, experience = activeExperience, win = root) {
    if (!experience) return legacyProduct;
    applyResolvedExperience(experience, win);
    return {
      ...legacyProduct,
      handle: String(legacyProduct?.handle || '').trim().toLowerCase(),
      steps: experience.projection.resolved_steps.map(stepForRenderer),
      _tacExperienceAuthority: publicState()
    };
  }

  function bootstrapProduct(runtime) {
    const product = runtimeProduct(runtime);
    if (!product) return null;
    return Object.freeze({ handle: product.handle, type: 'fixed', steps: Object.freeze([]) });
  }

  function coverPosition(id) {
    const match = String(id).match(/(?:^P|\.)(\d+)$/i);
    const numeric = Number(match?.[1]);
    if ([2, 10].includes(numeric)) return 2;
    if ([8, 11].includes(numeric)) return 8;
    if ([9, 12].includes(numeric)) return 9;
    return numeric;
  }

  function coverDesigns(selectedId) {
    if (!activeExperience) return null;
    const catalog = activeExperience.projection.cover_catalog;
    const copy = activeExperience.projection.localized_content.copy || {};
    const build = internalId => {
      const item = catalog.registry[internalId];
      const id = internalId.startsWith('legacy_')
        ? internalId.replace(/^legacy_/i, '').toUpperCase()
        : internalId;
      return Object.freeze({ id, position: coverPosition(id), label: copy[item.label_key] || id,
        src: activeExperience.assets[item.asset_id]?.url || '' });
    };
    const active = catalog.active_ids.map(build);
    const selected = String(selectedId || '').trim();
    const selectedInternal = [selected, selected.toLowerCase(), `legacy_${selected.toLowerCase()}`]
      .find(id => catalog.registry[id]);
    if (!catalog.registry[selectedInternal] || catalog.active_ids.includes(selectedInternal)) return active;
    const restored = build(selectedInternal);
    return active.map(item => item.position === restored.position ? restored : item);
  }

  function randomKey(win) {
    if (win.crypto?.randomUUID) return `pe-${win.crypto.randomUUID()}`;
    const bytes = new Uint8Array(24); win.crypto.getRandomValues(bytes);
    return `pe-${Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('')}`;
  }

  function idempotencyKey(win, runtime) {
    const key = `${CREATE_KEY_PREFIX}${runtime.product_gid}`;
    try {
      const saved = win.sessionStorage.getItem(key);
      if (saved) return saved;
      const created = randomKey(win);
      win.sessionStorage.setItem(key, created);
      return created;
    } catch { return randomKey(win); }
  }

  async function prepareDraftCreation(win = root) {
    const runtime = win.TAC_3C_ADOPTION_RUNTIME;
    const product = runtimeProduct(runtime);
    if (!shouldRequestAdoption(runtime, win.location, win.TAC_CUSTOMIZER_ROUTE)) {
      win.TAC_PRODUCT_EXPERIENCE_STATE = publicState('LEGACY');
      return null;
    }
    const candidateKey = `${product.environment}\n${product.gid}\n${product.handle}\n${String(runtime.locale || 'es').toLowerCase()}\n${String(runtime.rc_compatibility || 'false')}`;
    if (!candidate || candidate.key !== candidateKey) {
      const response = await win.fetch(runtimeUrl(runtime), {
        method: 'GET', headers: { Accept: 'application/json' }, credentials: 'omit', cache: 'no-store'
      });
      const data = await response.json();
      if (response.ok && data?.ok === true && data.mode === 'LEGACY') {
        candidate = null;
        win.TAC_PRODUCT_EXPERIENCE_STATE = publicState('LEGACY');
        return null;
      }
      const acceptedMode = ['CUSTOMER_RUNTIME', 'RC_COMPATIBILITY', 'ROLLBACK_READER', 'DEV_RUNTIME_ADOPTION'].includes(data?.mode);
      if (!response.ok || data?.ok !== true || !acceptedMode ||
          String(data.environment || data.experience?.environment || '') !== product.environment ||
          data.product?.handle !== product.handle || data.product?.product_gid !== product.gid)
        throw new Error(data?.error || 'experience_runtime_resolution_failed');
      candidate = Object.freeze({
        key: candidateKey,
        adoptable: !['RC_COMPATIBILITY', 'ROLLBACK_READER'].includes(data.mode) && data.adoptable !== false,
        experience: assertExperience(data.experience)
      });
      if (!candidate.adoptable) applyResolvedExperience(candidate.experience, win);
    }
    if (!candidate.adoptable) throw new Error(
      String(runtime?.rollback_reader_only || '') === 'true'
        ? 'experience_rollback_reader_only'
        : 'experience_rc_compatibility_read_only'
    );
    return Object.freeze({
      adopt: true,
      product_gid: runtime.product_gid,
      expected_activation_revision: Number(candidate.experience.binding.activation_revision),
      idempotency_key: idempotencyKey(win, runtime)
    });
  }

  function acceptDraftExperience(experience, win = root) {
    if (!experience) {
      activeExperience = null;
      announce(win);
      return null;
    }
    return applyResolvedExperience(experience, win);
  }

  function initialize(win = root) {
    win.TAC_PRODUCT_EXPERIENCE_STATE = publicState('LEGACY');
    const runtime = win.TAC_3C_ADOPTION_RUNTIME;
    if ((String(runtime?.rc_compatibility || '') === 'true' ||
        String(runtime?.rollback_reader_only || '') === 'true') &&
        shouldRequestAdoption(runtime, win.location, win.TAC_CUSTOMIZER_ROUTE)) {
      Promise.resolve(prepareDraftCreation(win)).catch(error => {
        if (!['experience_rc_compatibility_read_only', 'experience_rollback_reader_only'].includes(error?.message))
          win.console?.warn?.('[TAC] Product Experience RC compatibility unavailable');
      });
    }
    return win.TAC_PRODUCT_EXPERIENCE_STATE;
  }

  return Object.freeze({
    version: 'tac-update-3c-friendship-products.v1',
    shouldRequestAdoption, runtimeUrl, assertExperience, prepareDraftCreation,
    acceptDraftExperience, applyResolvedExperience, adaptProduct, bootstrapProduct, coverDesigns,
    assetOrigins, primeAssetOrigins, resolvedExampleImages, exampleImage, publicState, initialize,
    constants: Object.freeze({ THEME_ID, THEME_IDS, API_BASES, ASSET_HOSTS, RC_FLAG,
      EXAMPLE_RENDERER_KEYS, EXAMPLE_SEMANTIC_BY_RENDERER })
  });
});
